import { useState } from "react";
import { useMutation } from "@apollo/client";
import { useSnackbar } from "notistack";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import ReplayIcon from "@mui/icons-material/Replay";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import {
  CleanQueueMutation,
  RetryQueueMutation,
  DeleteQueuesMutation,
  type QueueRow,
} from "@/admin/entities/queues";
import { ConfirmDialog } from "@/admin/shared/ui";
import { cleanStatuses } from "../model";

/**
 * Отложенное действие, ждущее подтверждения в диалоге. Через диалог идут все
 * три пункта меню, а не только очистка и удаление: перезапуск упавших тоже
 * необратим — задачи уходят в очередь и начинают выполняться, отменить это
 * уже нечем.
 */
type PendingAction =
  | { kind: "clean"; status: string; label: string }
  | { kind: "retry" }
  | { kind: "delete" };

interface QueueMenuProps {
  queue: QueueRow;
}

export function QueueMenu({ queue }: QueueMenuProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [pending, setPending] = useState<PendingAction | null>(null);

  const onCompleted = (message: string) => () =>
    enqueueSnackbar(message, { variant: "success" });

  const onError = (error: Error) =>
    enqueueSnackbar(error.message, { variant: "error" });

  const [cleanQueue, cleanQueueResult] = useMutation(CleanQueueMutation, {
    onCompleted: onCompleted("Очередь очищена"),
    onError,
  });

  const [retryQueue, retryQueueResult] = useMutation(RetryQueueMutation, {
    variables: { queueName: queue.name },
    onCompleted: onCompleted("Упавшие задачи перезапущены"),
    onError,
  });

  const [deleteQueues, deleteQueuesResult] = useMutation(DeleteQueuesMutation, {
    variables: { names: [queue.name] },
    onCompleted: onCompleted("Очередь удалена"),
    onError,
    refetchQueries: ["QueuesListQuery"],
  });

  const handleClose = () => setAnchorEl(null);

  const handleClickClean = (status: string, label: string) => () => {
    setPending({ kind: "clean", status, label });
    handleClose();
  };

  const handleClickRetry = () => {
    setPending({ kind: "retry" });
    handleClose();
  };

  const handleClickDelete = () => {
    setPending({ kind: "delete" });
    handleClose();
  };

  const handleConfirm = () => {
    if (!pending) return;

    switch (pending.kind) {
      case "clean":
        cleanQueue({
          variables: { queueName: queue.name, status: pending.status },
        });
        break;
      case "retry":
        retryQueue();
        break;
      case "delete":
        deleteQueues();
        break;
    }

    setPending(null);
  };

  const getConfirmTexts = (action: PendingAction) => {
    switch (action.kind) {
      case "clean":
        return {
          title: action.label,
          text: `Задачи очереди «${queue.name}» в этом статусе будут удалены. Действие необратимо.`,
        };
      case "retry":
        return {
          title: "Перезапустить упавшие",
          text: `Все упавшие задачи очереди «${queue.name}» вернутся в очередь и начнут выполняться. Действие необратимо.`,
        };
      case "delete":
        return {
          title: "Удалить очередь",
          text: `Очередь «${queue.name}» будет удалена. Действие необратимо.`,
        };
    }
  };

  const confirm = pending ? getConfirmTexts(pending) : null;

  return (
    <>
      <IconButton
        onClick={(event) => setAnchorEl(event.currentTarget)}
        size="large"
      >
        <MoreVertIcon />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {cleanStatuses.map(({ status, label }) => (
          <MenuItem
            key={status}
            onClick={handleClickClean(status, label)}
            disabled={cleanQueueResult.loading}
          >
            <ListItemIcon>
              <DeleteIcon />
            </ListItemIcon>
            <ListItemText>{label}</ListItemText>
          </MenuItem>
        ))}

        <MenuItem
          onClick={handleClickRetry}
          disabled={retryQueueResult.loading}
        >
          <ListItemIcon>
            <ReplayIcon />
          </ListItemIcon>
          <ListItemText>Перезапустить упавшие</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={handleClickDelete}
          disabled={deleteQueuesResult.loading}
        >
          <ListItemIcon>
            <DeleteForeverIcon />
          </ListItemIcon>
          <ListItemText>Удалить очередь</ListItemText>
        </MenuItem>
      </Menu>

      <ConfirmDialog
        open={pending !== null}
        onCancel={() => setPending(null)}
        onConfirm={handleConfirm}
        loading={
          cleanQueueResult.loading ||
          retryQueueResult.loading ||
          deleteQueuesResult.loading
        }
        confirmTitle={confirm?.title}
        confirmText={confirm?.text}
      />
    </>
  );
}
